import { useState } from "react";
import { AlertTriangle, Check, Hash } from "lucide-react";
import {
  formatStartNumberArithmetic,
  startNumberSource,
  startNumberStalenessWarning,
} from "@shared-core/domain/startNumber";
import type { NextStartNumber } from "@shared-core/data/api-types";
import { Button } from "../../primitives/button";
import { Card } from "../../primitives/card";

/** The opening-number entry, for a numbered series with nothing known yet. */
export interface OpeningStartNumberEntry {
  saving: boolean;
  /** The server's field error on `opening_start_number`, if any. */
  error: string | null;
  onSave: (value: string) => void;
}

/**
 * The proposed next start number of a numbered item (NCR sets, pads and
 * books). CIS proposes it from the item's order lines, else its opening
 * number, else Tharstern; staff confirm it on every order, so it never
 * applies itself: Apply writes it into the Start number field, which stays
 * editable. With nothing known yet, a numbered series asks for its opening
 * number instead (the only start number staff ever store).
 */
export function StartNumberPanel({
  proposal,
  canApply,
  applied,
  onApply,
  opening,
}: {
  proposal: NextStartNumber | null;
  /** False when the item has no Start number field: the panel still shows
   *  the proposal, just without an Apply affordance. */
  canApply: boolean;
  applied: boolean;
  onApply: () => void;
  opening?: OpeningStartNumberEntry;
}) {
  return (
    <Card className="p-4 space-y-2 border-[color:var(--color-primary)]/30">
      <div className="flex items-center gap-2">
        <Hash className="size-4 text-[color:var(--color-primary)]" />
        <div className="text-sm font-medium">Next start number</div>
      </div>
      {proposal ? (
        <Proposal
          proposal={proposal}
          canApply={canApply}
          applied={applied}
          onApply={onApply}
        />
      ) : opening ? (
        <OpeningEntry opening={opening} />
      ) : null}
    </Card>
  );
}

function Proposal({
  proposal,
  canApply,
  applied,
  onApply,
}: {
  proposal: NextStartNumber;
  canApply: boolean;
  applied: boolean;
  onApply: () => void;
}) {
  const arithmetic = formatStartNumberArithmetic(proposal);
  const warning = startNumberStalenessWarning(proposal);
  return (
    <>
      <p className="text-sm">
        <span className="font-medium tabular-nums">{proposal.next_start}</span>{" "}
        <span className="text-muted-foreground">
          {startNumberSource(proposal)}
        </span>
      </p>
      {arithmetic ? (
        <p className="text-sm text-muted-foreground">{arithmetic}</p>
      ) : null}
      {warning ? (
        <div className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          <AlertTriangle className="size-3.5 shrink-0 mt-0.5" />
          <span>{warning}</span>
        </div>
      ) : null}
      {canApply ? (
        <Button
          type="button"
          size="sm"
          variant={applied ? "outline" : "default"}
          onClick={onApply}
          disabled={applied}
        >
          {applied ? (
            <>
              <Check className="size-3.5 mr-1" /> Applied {proposal.next_start}
            </>
          ) : (
            <>Apply {proposal.next_start}</>
          )}
        </Button>
      ) : null}
    </>
  );
}

function OpeningEntry({ opening }: { opening: OpeningStartNumberEntry }) {
  const [value, setValue] = useState("");
  const hintId = "opening-start-number-hint";
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        opening.onSave(value);
      }}
    >
      <p className="text-sm text-muted-foreground">
        No start number is known for this series yet. Enter the number the
        series continues from.
      </p>
      <div className="flex items-center gap-2">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputMode="numeric"
          maxLength={12}
          aria-label="Opening start number"
          aria-invalid={opening.error ? true : undefined}
          aria-describedby={opening.error ? hintId : undefined}
          className="px-3 py-1.5 rounded-md border border-border bg-card text-sm w-40 tabular-nums"
        />
        <Button
          type="submit"
          size="sm"
          disabled={opening.saving || !value.trim()}
        >
          Save opening number
        </Button>
      </div>
      {opening.error ? (
        <p id={hintId} className="text-sm text-red-600">
          {opening.error}
        </p>
      ) : null}
    </form>
  );
}
